import UnavailableDeliveryMethod from '@app/pdp/components/AddToCart/DeliveryMethods/UnavailableDeliveryMethod';
import type {
    SameDayDeliveryProps,
    SddForm,
} from '@app/pdp/components/AddToCart/DeliveryMethods/types';
import { getAvailableQuantities } from '@app/pdp/components/AddToCart/DeliveryMethods/utils';
import AddToCartBtn from '@app/pdp/components/AddToCart/common/AddToCartBtn';
import MinimumQuantityLabel from '@app/pdp/components/AddToCart/common/MinimumQuantityLabel';
import { useAddToCTCart } from '@app/pdp/components/AddToCart/hooks/useAddToCTCart';
import { EstimatedTreatPoints } from '@app/pdp/components/EstimatedTreatPoints';
import { DELIVERY_METHODS } from '@app/pdp/constants';
import { useProductDetails } from '@app/pdp/hooks';
import { getEstimationDelivery } from '@app/pdp/utils';
import { clsx, useEffectOnce, useOptimizely } from '@libs/common-next';
import {
    safeJsonParse,
    stringifyStoreId,
    getCountry,
    DeliveryType,
} from '@libs/core';
import type { DoorDashAPIBody } from '@libs/doordash';
import { hasStockType } from '@libs/shared-ui';
import {
    Button,
    RadioField,
    SelectField,
    StockStatusEnum,
    StockStatusLabel,
    TextPassage,
} from '@petsmart-ui/sparky';
import isEqual from 'lodash.isequal';
import type { MouseEvent } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import AddSddAddrForm from './AddSddAddrForm';
import './SameDayDelivery.scss';
import { LOCAL_STORAGE } from './constants';

const getDoorDashBody = (address: SddForm, productId: string) => {
    const destination: DoorDashAPIBody = {
        dropoff_address: {
            street: address.street || '',
            unit: address.complement || '',
            city: address.city || '',
            state: address.state || '',
            zip_code: address.zip || '',
        },
        order_value: 3100,
        pickup_time: new Date().toISOString(),
        productId,
    };

    return destination;
};

// ! (need fix) `ItemAfter` -> Move to a separate component
interface ItemAfterProps {
    isDisabled: boolean;
    isPageLoading: boolean;
    isPending: boolean;
    isLoadingStoreInfo: boolean;
    isSddAddress: boolean;
    stockStatus: StockStatusEnum;
}

function ItemAfter({
    isDisabled,
    isSddAddress,
    isPending,
    isPageLoading,
    isLoadingStoreInfo,
    stockStatus,
}: Readonly<ItemAfterProps>) {
    if (isDisabled && !isPageLoading) {
        return (
            <TextPassage className="delivery-method-status sparky-u-color-utility-warning">
                Not available for this item
            </TextPassage>
        );
    }

    if (!isDisabled && isPending) {
        return <div data-testid="stock-status" className="stock-status" />;
    }

    if (isSddAddress && !isLoadingStoreInfo) {
        return (
            <div data-testid="stock-status" className="stock-status">
                <StockStatusLabel
                    labelFewLeftInStock="Only a few left"
                    stockStatus={stockStatus}
                />
            </div>
        );
    }

    return null;
}

export default function SameDayDelivery({
    stockStatus,
    isAvailable,
    isDisabled,
    minimumCaseQty,
    value,
    isDisabledByPersonalization,
    product,
    inventoryQuantity,
    isPageLoading,
}: Readonly<SameDayDeliveryProps>) {
    const { selectedProduct, sddStoreId, setSddStoreId } = useProductDetails();
    const [selectedQty, setSelectedQty] = useState(minimumCaseQty || 1);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [sddAddress, setSddAddress] = useState<SddForm | undefined>();
    const [isAvailableState, setIsAvailableState] = useState(isAvailable);
    const [addedToCart, setAddedToCart] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingStoreInfo, setIsLoadingStoreInfo] = useState(false);
    const { addToCTCart, isLoading: isCTLoading } = useAddToCTCart();

    const country = getCountry();

    const isValidAddress = !!sddAddress?.isValid;

    const onQuantityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const qty = event?.target.value;

        if (qty) {
            setSelectedQty(parseInt(qty, 10));
        }
    };

    const availableQuantities = getAvailableQuantities(
        minimumCaseQty,
        inventoryQuantity,
        selectedProduct.maxOrderQty
    );

    const handlePickUpStore = useCallback(
        async (address?: SddForm, shouldUpdateLocalStorage?: boolean) => {
            let isValid = false;

            if (address) {
                setIsLoadingStoreInfo(true);
                const destination = getDoorDashBody(
                    address,
                    selectedProduct.objectID
                );
                const result = await getEstimationDelivery(destination);

                if (result?.success) {
                    setSddStoreId(result.storeId);
                    isValid = true;
                }

                if (!isEqual(address, sddAddress)) {
                    setSddAddress({ ...address, isValid });
                }

                if (shouldUpdateLocalStorage) {
                    localStorage.setItem(
                        LOCAL_STORAGE.SDD_ADDR,
                        JSON.stringify({ ...address, isValid })
                    );
                }

                setTimeout(() => {
                    setIsLoadingStoreInfo(false);
                }, 1000);
            }

            return isValid;
        },
        [sddAddress, selectedProduct.objectID, setSddStoreId]
    );

    useEffectOnce(() => {
        const sddAddr = safeJsonParse<SddForm>(
            localStorage.getItem(LOCAL_STORAGE.SDD_ADDR)
        );

        setSddAddress(sddAddr || undefined);
    });
    // Added logic for min quantity for optimizely tile
    useEffect(() => {
        if (minimumCaseQty != null && selectedQty !== minimumCaseQty) {
            setSelectedQty(minimumCaseQty || 1);
        }
    }, [minimumCaseQty]);

    useEffect(() => {
        handlePickUpStore(sddAddress);
    }, [handlePickUpStore, sddAddress]);

    useEffect(() => {
        setIsLoading(isCTLoading);
    }, [isCTLoading]);

    const openDrawer = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsDrawerOpen(true);
    };
    const closeDrawer = (hasErrors: boolean) => {
        setIsDrawerOpen(false);
        setIsAvailableState(!hasErrors);
    };

    const userContext = useOptimizely().context; // Optimizely

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);

        try {
            await addToCTCart({
                country,
                sku: `${product.id}`,
                quantity: selectedQty,
                fulfillmentMethod: DeliveryType.SDD,
                storeId: stringifyStoreId(sddStoreId),
                dropOffAddress: {
                    addressLine1: sddAddress?.street ?? '',
                    addressLine2: sddAddress?.complement ?? '',
                    city: sddAddress?.city ?? '',
                    stateProvince: sddAddress?.state ?? '',
                    postalCode: sddAddress?.zip ?? '',
                    country,
                },
            });

            // this only executes for success case
            setAddedToCart(true);
            userContext?.trackEvent('sdd_atc__ot_'); // Optimizely
        } finally {
            setIsLoading(false);
        }
    };

    const deliveryMethodsProps = {
        isPageLoading: Boolean(isPageLoading),
        deliveryMethod: 'Same-day delivery',
    };

    if (isDisabledByPersonalization) {
        return <UnavailableDeliveryMethod {...deliveryMethodsProps} />;
    }

    const addToCartText = addedToCart
        ? DELIVERY_METHODS.ADDED_TO_CART
        : DELIVERY_METHODS.ADD_TO_CART;
    return (
        <RadioField.Item
            className="sdd"
            disabled={isDisabled}
            value={value}
            text={
                <span className="radio-field-title sparky-u-theme-typography-body-lg-bold">
                    Same-day delivery
                </span>
            }
            itemAfter={
                /* New */
                <ItemAfter
                    isDisabled={isDisabled}
                    isPageLoading={isPageLoading}
                    isSddAddress={Boolean(sddAddress)}
                    isPending={stockStatus === 'Pending'}
                    isLoadingStoreInfo={isLoadingStoreInfo}
                    stockStatus={
                        isAvailableState && isValidAddress
                            ? (stockStatus as StockStatusEnum)
                            : StockStatusEnum.NotAvailable
                    }
                />
            }
            hiddenContent={
                <form onSubmit={onSubmit}>
                    {isAvailableState && sddAddress?.street ? (
                        <div className="sdd-address-selection">
                            <TextPassage>
                                <span>Delivering to </span>
                                <span
                                    className="sdd-current-address"
                                    data-cs-mask
                                >
                                    {sddAddress.street}
                                </span>
                            </TextPassage>
                            <Button
                                loading={isPageLoading}
                                text="Change"
                                variant="link"
                                className="change-address-btn"
                                data-testid="sdd-change-address"
                                onClick={(e: MouseEvent<HTMLButtonElement>) =>
                                    openDrawer(e)
                                }
                            />
                        </div>
                    ) : (
                        <div className="sdd-address-selection">
                            <TextPassage className="check-availability">
                                Add an address to check availability
                            </TextPassage>
                            <Button
                                loading={isPageLoading}
                                text="Add address"
                                variant="link"
                                className="sparky-u-margin-bottom-sm"
                                data-testid="sdd-add-address"
                                onClick={(e: MouseEvent<HTMLButtonElement>) =>
                                    openDrawer(e)
                                }
                            />
                        </div>
                    )}
                    {isAvailableState &&
                        sddAddress &&
                        isValidAddress &&
                        hasStockType(stockStatus) && (
                            <div data-testid="in-stock-fields">
                                <div className="sdd-info">
                                    <TextPassage>
                                        Select a delivery window at checkout
                                    </TextPassage>

                                    <MinimumQuantityLabel
                                        minimumCaseQty={minimumCaseQty}
                                    />
                                </div>

                                <SelectField
                                    value={selectedQty}
                                    onChange={onQuantityChange}
                                    label="Quantity"
                                    items={availableQuantities}
                                    disabled={availableQuantities.length < 1}
                                />

                                {hasStockType(stockStatus) && (
                                    <EstimatedTreatPoints qty={selectedQty} />
                                )}
                                <br />
                            </div>
                        )}
                    {sddAddress ? (
                        <AddToCartBtn
                            text={addToCartText}
                            className={clsx(
                                addedToCart &&
                                    'add-to-cart-button add-to-cart-button__green'
                            )}
                            setAddedToCart={setAddedToCart}
                            addedToCart={addedToCart}
                            disabled={
                                !isAvailableState ||
                                !isValidAddress ||
                                !hasStockType(stockStatus)
                            }
                            isLoading={isLoading || isPageLoading}
                        />
                    ) : null}
                    <AddSddAddrForm
                        isDrawerOpen={isDrawerOpen}
                        closeDrawer={closeDrawer}
                        isAvailable={isAvailableState}
                        sddAddress={sddAddress}
                        onAddressSubmit={handlePickUpStore}
                        setSddAddress={setSddAddress}
                    />
                </form>
            }
        />
    );
}
